import { cn } from '@/lib/utils'
import type { AccomplishmentData } from '@/hooks/useVisualTools'

interface AccomplishmentCelebrationProps {
  data: AccomplishmentData
  className?: string
}

// Sparkle burst particle - radiates outward from center
function BurstSparkle({ angle, delay, distance }: { angle: number; delay: number; distance: number }) {
  const x = Math.cos((angle * Math.PI) / 180) * distance
  const y = Math.sin((angle * Math.PI) / 180) * distance

  return (
    <div
      className="absolute left-1/2 top-1/2 w-1.5 h-1.5 rounded-full bg-amber-300 animate-burst-sparkle"
      style={{
        '--burst-x': `${x}px`,
        '--burst-y': `${y}px`,
        animationDelay: `${delay}ms`,
      } as React.CSSProperties}
    />
  )
}

// Floating shimmer particle
function ShimmerParticle({ left, delay, size }: { left: number; delay: number; size: number }) {
  return (
    <div
      className="absolute rounded-full bg-gradient-to-br from-amber-200 to-yellow-400 animate-shimmer opacity-0"
      style={{
        left: `${left}%`,
        top: `${20 + Math.random() * 60}%`,
        width: `${size}px`,
        height: `${size}px`,
        animationDelay: `${delay}ms`,
      }}
    />
  )
}

// Enhanced confetti with varied shapes
function ConfettiPiece({
  delay,
  color,
  left,
  shape,
  size,
  swayDirection
}: {
  delay: number
  color: string
  left: number
  shape: 'circle' | 'diamond' | 'star' | 'rectangle'
  size: number
  swayDirection: 1 | -1
}) {
  const shapeClasses = {
    circle: 'rounded-full',
    diamond: 'rotate-45',
    star: 'clip-star',
    rectangle: 'rounded-sm',
  }

  return (
    <div
      className={cn(
        'absolute animate-confetti-enhanced opacity-0',
        shapeClasses[shape]
      )}
      style={{
        backgroundColor: color,
        left: `${left}%`,
        top: '-20px',
        width: `${size}px`,
        height: shape === 'rectangle' ? `${size * 1.5}px` : `${size}px`,
        animationDelay: `${delay}ms`,
        '--sway-direction': swayDirection,
      } as React.CSSProperties}
    />
  )
}

// Pre-computed burst sparkles (12 points radiating outward)
const BURST_SPARKLES = Array.from({ length: 12 }, (_, i) => ({
  id: i,
  angle: i * 30,
  delay: i * 30,
  distance: 60 + (i % 2) * 20,
}))

// Secondary burst layer
const BURST_SPARKLES_OUTER = Array.from({ length: 8 }, (_, i) => ({
  id: i + 12,
  angle: i * 45 + 22.5,
  delay: 100 + i * 40,
  distance: 90,
}))

// Shimmer particles
const SHIMMER_PARTICLES = Array.from({ length: 8 }, (_, i) => ({
  id: i,
  left: 10 + i * 12,
  delay: 300 + i * 150,
  size: 4 + (i % 3) * 2,
}))

// Enhanced confetti with variety
const CONFETTI_PIECES: Array<{
  id: number
  delay: number
  color: string
  left: number
  shape: 'circle' | 'diamond' | 'star' | 'rectangle'
  size: number
  swayDirection: 1 | -1
}> = [
  // Warm golds and ambers
  { id: 0, delay: 0, color: '#F59E0B', left: 5, shape: 'circle', size: 8, swayDirection: 1 },
  { id: 1, delay: 80, color: '#FBBF24', left: 15, shape: 'diamond', size: 10, swayDirection: -1 },
  { id: 2, delay: 160, color: '#FCD34D', left: 25, shape: 'rectangle', size: 6, swayDirection: 1 },
  { id: 3, delay: 240, color: '#F59E0B', left: 35, shape: 'circle', size: 12, swayDirection: -1 },
  { id: 4, delay: 320, color: '#D97706', left: 45, shape: 'diamond', size: 8, swayDirection: 1 },
  // Coral and warm pinks
  { id: 5, delay: 100, color: '#FB7185', left: 55, shape: 'circle', size: 10, swayDirection: -1 },
  { id: 6, delay: 180, color: '#FDA4AF', left: 65, shape: 'rectangle', size: 8, swayDirection: 1 },
  { id: 7, delay: 260, color: '#F43F5E', left: 75, shape: 'diamond', size: 6, swayDirection: -1 },
  { id: 8, delay: 340, color: '#FB7185', left: 85, shape: 'circle', size: 14, swayDirection: 1 },
  { id: 9, delay: 420, color: '#FDA4AF', left: 95, shape: 'rectangle', size: 10, swayDirection: -1 },
  // Second wave - more gold accents
  { id: 10, delay: 50, color: '#FDE68A', left: 10, shape: 'diamond', size: 6, swayDirection: -1 },
  { id: 11, delay: 130, color: '#FBBF24', left: 20, shape: 'circle', size: 8, swayDirection: 1 },
  { id: 12, delay: 210, color: '#F59E0B', left: 30, shape: 'rectangle', size: 12, swayDirection: -1 },
  { id: 13, delay: 290, color: '#FCD34D', left: 40, shape: 'circle', size: 10, swayDirection: 1 },
  { id: 14, delay: 370, color: '#D97706', left: 50, shape: 'diamond', size: 8, swayDirection: -1 },
  { id: 15, delay: 150, color: '#FBBF24', left: 60, shape: 'circle', size: 6, swayDirection: 1 },
  { id: 16, delay: 230, color: '#FDE68A', left: 70, shape: 'rectangle', size: 10, swayDirection: -1 },
  { id: 17, delay: 310, color: '#F59E0B', left: 80, shape: 'diamond', size: 12, swayDirection: 1 },
  { id: 18, delay: 390, color: '#FBBF24', left: 90, shape: 'circle', size: 8, swayDirection: -1 },
  { id: 19, delay: 470, color: '#FCD34D', left: 8, shape: 'rectangle', size: 6, swayDirection: 1 },
]

export function AccomplishmentCelebration({ data, className }: AccomplishmentCelebrationProps) {
  return (
    <div className="relative">
      {/* Outer glow aura */}
      <div className="absolute -inset-4 rounded-3xl bg-gradient-to-r from-amber-400/20 via-yellow-300/30 to-orange-400/20 blur-xl animate-glow-pulse" />

      {/* Confetti layer - falls from above */}
      <div className="absolute -inset-16 pointer-events-none overflow-hidden">
        {CONFETTI_PIECES.map((piece) => (
          <ConfettiPiece
            key={piece.id}
            delay={piece.delay}
            color={piece.color}
            left={piece.left}
            shape={piece.shape}
            size={piece.size}
            swayDirection={piece.swayDirection}
          />
        ))}
      </div>

      {/* Card */}
      <div
        className={cn(
          'relative rounded-2xl p-8 shadow-2xl overflow-hidden',
          'bg-gradient-to-br from-amber-50 via-yellow-50/90 to-orange-50',
          'border border-amber-200/60',
          'animate-celebration-entrance',
          className
        )}
      >
        {/* Inner shimmer overlay */}
        <div className="absolute inset-0 bg-gradient-to-tr from-transparent via-white/40 to-transparent animate-shimmer-sweep pointer-events-none" />

        {/* Burst sparkles from center */}
        <div className="absolute inset-0 pointer-events-none">
          {BURST_SPARKLES.map((sparkle) => (
            <BurstSparkle
              key={sparkle.id}
              angle={sparkle.angle}
              delay={sparkle.delay}
              distance={sparkle.distance}
            />
          ))}
          {BURST_SPARKLES_OUTER.map((sparkle) => (
            <BurstSparkle
              key={sparkle.id}
              angle={sparkle.angle}
              delay={sparkle.delay}
              distance={sparkle.distance}
            />
          ))}
        </div>

        {/* Floating shimmer particles */}
        <div className="absolute inset-0 pointer-events-none">
          {SHIMMER_PARTICLES.map((particle) => (
            <ShimmerParticle
              key={particle.id}
              left={particle.left}
              delay={particle.delay}
              size={particle.size}
            />
          ))}
        </div>

        {/* Content */}
        <div className="relative z-10 text-center">
          {/* Glowing star icon */}
          <div className="mb-4 inline-flex items-center justify-center relative">
            {/* Rotating ring */}
            <div className="absolute w-20 h-20 rounded-full border-2 border-dashed border-amber-300/50 animate-spin-slow" />

            {/* Main icon container */}
            <div className="relative w-16 h-16 rounded-full bg-gradient-to-br from-amber-400 via-yellow-400 to-orange-500 shadow-lg shadow-amber-400/40 flex items-center justify-center animate-icon-bounce">
              {/* Inner glow */}
              <div className="absolute inset-1 rounded-full bg-gradient-to-br from-yellow-200/60 to-transparent" />

              {/* Star icon */}
              <svg
                xmlns="http://www.w3.org/2000/svg"
                width="28"
                height="28"
                viewBox="0 0 24 24"
                fill="white"
                stroke="white"
                strokeWidth="1"
                className="relative z-10 drop-shadow-sm"
              >
                <polygon points="12 2 15.09 8.26 22 9.27 17 14.14 18.18 21.02 12 17.77 5.82 21.02 7 14.14 2 9.27 8.91 8.26 12 2" />
              </svg>
            </div>
          </div>

          {/* Celebration message */}
          <p className="text-2xl font-bold bg-gradient-to-r from-amber-700 via-orange-600 to-amber-700 bg-clip-text text-transparent animate-text-reveal">
            {data.message}
          </p>

          {/* Decorative sparkle row */}
          <div className="mt-3 flex justify-center gap-2">
            {[0, 1, 2, 3, 4].map((i) => (
              <span
                key={i}
                className="animate-sparkle-twinkle"
                style={{ animationDelay: `${i * 150}ms` }}
              >
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  width={i === 2 ? 18 : 14}
                  height={i === 2 ? 18 : 14}
                  viewBox="0 0 24 24"
                  fill="currentColor"
                  className={i === 2 ? 'text-amber-500' : 'text-amber-400/70'}
                >
                  <path d="m12 3-1.9 5.8a2 2 0 0 1-1.3 1.3L3 12l5.8 1.9a2 2 0 0 1 1.3 1.3L12 21l1.9-5.8a2 2 0 0 1 1.3-1.3L21 12l-5.8-1.9a2 2 0 0 1-1.3-1.3z" />
                </svg>
              </span>
            ))}
          </div>
        </div>
      </div>
    </div>
  )
}
